import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import type { Control, UseFormWatch, UseFormSetValue } from 'react-hook-form'
import type { DataAccessFormValues, WhoMode } from '../../types'

interface SectionWhoProps {
    control: Control<DataAccessFormValues>
    watch: UseFormWatch<DataAccessFormValues>
    setValue: UseFormSetValue<DataAccessFormValues>
}

const WHO_OPTIONS: { value: WhoMode; labelKey: string }[] = [
    { value: 'employees', labelKey: 'data-access-who-employees' },
    { value: 'departments', labelKey: 'data-access-who-departments' },
    { value: 'company', labelKey: 'data-access-who-company' },
]

/**
 * Section 1 — WHO is this rule applied to?
 * Radio: employees | departments | company + collapsible ignores
 */
const SectionWho = ({ control, watch }: SectionWhoProps) => {
    const { t } = useTranslation('settings')
    const whoMode = watch('who_mode')
    const [ignoresOpen, setIgnoresOpen] = useState(false)

    return (
        <div className="bg-white rounded-lg p-6 mb-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
                {t('data-access-section-who-title')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
                {t('data-access-section-who-helper')}
            </p>

            {/* Radio group */}
            <Controller
                control={control}
                name="who_mode"
                render={({ field, fieldState }) => (
                    <div className="space-y-2 mb-4">
                        {WHO_OPTIONS.map((opt) => (
                            <label
                                key={opt.value}
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="radio"
                                    value={opt.value}
                                    checked={field.value === opt.value}
                                    onChange={() => field.onChange(opt.value)}
                                    className="accent-primary"
                                />
                                <span className="text-sm text-gray-700">
                                    {t(opt.labelKey)}
                                </span>
                            </label>
                        ))}
                        {fieldState.error && (
                            <p className="text-xs text-red-500">
                                {fieldState.error.message}
                            </p>
                        )}
                    </div>
                )}
            />

            {/* Conditional input */}
            {whoMode === 'employees' && (
                <div className="mt-2">
                    <EmployeeSelect
                        control={control}
                        name="assignees"
                        multiple
                        label="data-access-assignees"
                    />
                </div>
            )}
            {whoMode === 'departments' && (
                <div className="mt-2">
                    <DepartmentSelect
                        control={control}
                        name="who_departments"
                        label="data-access-departments"
                    />
                </div>
            )}
            {whoMode === 'company' && (
                <p className="text-sm text-gray-500 italic mt-2">
                    {t('data-access-who-company-hint')}
                </p>
            )}

            {/* Collapsible: exclude some people */}
            <div className="mt-4 border-t pt-3">
                <button
                    type="button"
                    onClick={() => setIgnoresOpen((o) => !o)}
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                    <span>{ignoresOpen ? '▾' : '▸'}</span>
                    {t('data-access-exclude-people')}
                </button>
                {ignoresOpen && (
                    <div className="mt-3">
                        <EmployeeSelect
                            control={control}
                            name="ignores"
                            multiple
                            label="data-access-ignores"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default SectionWho
